/**
 * AWS Analytics Service
 * Provides global statistics for the Admin Dashboard
 */

import {
    DynamoDBDocumentClient,
    ScanCommand
} from '@aws-sdk/lib-dynamodb';
import awsConfig from '../config/aws-config';
import { getTrackedDynamoDBClient } from './aws-core';

export class AWSAnalyticsService {

    private getClient(): DynamoDBDocumentClient {
        return getTrackedDynamoDBClient();
    }

    /**
     * Get count of items in a table
     * Note: Scan is expensive, but for current scale it is acceptable for Admin dashboard.
     * Optimize later with dedicated counter tables or streams.
     */
    private async getCount(tableName: string): Promise<number> {
        try {
            const client = this.getClient();
            // We only need the count, so we can use Select: 'COUNT' if using low-level client, 
            // but DocumentClient Scan returns ScannedCount. 
            // Better to use limit or project only keys to save bandwidth.
            const command = new ScanCommand({
                TableName: tableName,
                Select: 'COUNT'
            });

            const response = await client.send(command);
            return response.Count || 0;
        } catch (error) {
            console.error(`Error counting table ${tableName}:`, error);
            return 0;
        }
    }

    async getTotalInvoices(): Promise<number> {
        return this.getCount(awsConfig.dynamodb.TABLES.INVOICES);
    }

    async getTotalClients(): Promise<number> {
        return this.getCount(awsConfig.dynamodb.TABLES.CLIENTS);
    }

    async getTotalInventory(): Promise<number> {
        return this.getCount(awsConfig.dynamodb.TABLES.INVENTORY);
    }

    async getTotalTransactions(): Promise<number> {
        return this.getCount(awsConfig.dynamodb.TABLES.TRANSACTIONS);
    }

    /**
     * Get resource usage stats for a specific user
     * Calculates storage based on item counts (approximate)
     */
    async getUserStats(userId: string): Promise<{
        invoices: number;
        clients: number;
        inventory: number;
        transactions: number;
        storageBytes: number;
    }> {
        const client = this.getClient();

        const countUserItems = async (tableName: string): Promise<number> => {
            try {
                // Using Scan with Filter for now. In production, this should use a GSI on userId.
                const command = new ScanCommand({
                    TableName: tableName,
                    FilterExpression: 'userId = :userId',
                    ExpressionAttributeValues: {
                        ':userId': userId
                    },
                    Select: 'COUNT'
                });
                const result = await client.send(command);
                return result.Count || 0;
            } catch (error) {
                console.error(`Error counting user items in ${tableName}:`, error);
                return 0;
            }
        };

        const [invoices, clients, inventory, transactions] = await Promise.all([
            countUserItems(awsConfig.dynamodb.TABLES.INVOICES),
            countUserItems(awsConfig.dynamodb.TABLES.CLIENTS),
            countUserItems(awsConfig.dynamodb.TABLES.INVENTORY),
            countUserItems(awsConfig.dynamodb.TABLES.TRANSACTIONS)
        ]);

        // Approximate size: 1KB per record on average for this estimation
        const AV_RECORD_SIZE_BYTES = 1024;
        const totalRecords = invoices + clients + inventory + transactions;
        const storageBytes = totalRecords * AV_RECORD_SIZE_BYTES;

        return {
            invoices,
            clients,
            inventory,
            transactions,
            storageBytes
        };
    }
}

export const awsAnalyticsService = new AWSAnalyticsService();
